import { methodAtLeastAsStrong } from "./method.js";
/** Tries per confirmation code (D-8): a third miss locks the code. */
export const CONFIRMATION_CODE_ATTEMPT_PER_CODE = 3;
/** Passcode misses per conversation (design §40.9.4): the third locks the conversation's confirmation. */
export const PASSCODE_MISS_PER_CONVERSATION = 3;
const NO_COUNT = Object.freeze({
    codeSentCount: 0,
    failedAttemptCount: 0,
    nonAttemptCount: 0,
    passcodeMissCount: 0,
    passcodeAskCount: 0,
});
/** A new conversation's state. */
export const PARTY_CONFIRMATION_UNCONFIRMED = Object.freeze({ kind: "unconfirmed", ...NO_COUNT });
function counterOf(state) {
    return {
        codeSentCount: state.codeSentCount,
        failedAttemptCount: state.failedAttemptCount,
        nonAttemptCount: state.nonAttemptCount,
        passcodeMissCount: state.passcodeMissCount,
        passcodeAskCount: state.passcodeAskCount,
    };
}
/** The confirmation the gate may rely on now: the confirmed state's, or a pending code's `prior`; none when locked. */
export function confirmationInForceOf(state) {
    if (state.kind === "confirmed") {
        return { method: state.method, everyMethod: state.everyMethod, confirmedAtMs: state.confirmedAtMs, stintOrdinal: state.stintOrdinal };
    }
    if (state.kind === "pending")
        return state.prior;
    return null;
}
function assertLimits(limits) {
    for (const [name, value] of Object.entries(limits)) {
        if (!Number.isSafeInteger(value) || value < 1)
            throw new RangeError(`the confirmation limit ${name} is a positive integer`);
    }
}
function merged(prior, method, atMs, stintOrdinal) {
    if (prior === null)
        return { method, everyMethod: Object.freeze([method]), confirmedAtMs: atMs, stintOrdinal };
    const everyMethod = prior.everyMethod.includes(method) ? prior.everyMethod : Object.freeze([...prior.everyMethod, method]);
    return methodAtLeastAsStrong(method, prior.method)
        ? { method, everyMethod, confirmedAtMs: atMs, stintOrdinal }
        : { ...prior, everyMethod };
}
function confirmedOf(fact, counter) {
    return Object.freeze({ kind: "confirmed", ...fact, ...counter });
}
/** Back to what held before the code: the prior confirmation, else unconfirmed. */
function backOf(prior, counter) {
    return prior === null ? Object.freeze({ kind: "unconfirmed", ...counter }) : confirmedOf(prior, counter);
}
function lockedOf(method, atMs, counter) {
    return Object.freeze({ kind: "locked", method, lockedAtMs: atMs, ...counter });
}
/** Whether an outcome event is about the code being awaited and not yet applied. */
function freshOutcome(state, event) {
    return (state.kind === "pending" &&
        state.method === event.method &&
        state.ordinal === event.ordinal &&
        event.outcomeOrdinal > state.outcomeCount);
}
/** A miss (or a non-attempt past the free ones) on the awaited code, the window's outcome ordinal applied. */
function missOf(state, counter, outcomeOrdinal, nonAttemptForCode, atMs, limits) {
    const missed = {
        ...counter,
        failedAttemptCount: counter.failedAttemptCount + 1,
        passcodeMissCount: state.method === "passcode" ? counter.passcodeMissCount + 1 : counter.passcodeMissCount,
    };
    const attemptsLeft = state.attemptsLeft - 1;
    if (state.method === "passcode" && missed.passcodeMissCount >= PASSCODE_MISS_PER_CONVERSATION) {
        return lockedOf("passcode", atMs, missed);
    }
    if (attemptsLeft <= 0) {
        if (state.method === "passcode" || missed.codeSentCount >= limits.codePerConversation) {
            return lockedOf(state.method, atMs, missed);
        }
        return backOf(state.prior, missed);
    }
    return Object.freeze({ ...state, ...missed, attemptsLeft, outcomeCount: outcomeOrdinal, nonAttemptForCode });
}
/**
 * The state after one event (design §40.9.1). Pure and idempotent per `(code ordinal, outcome ordinal)`; an event that
 * does not apply to the current state returns it unchanged. A locked state never moves.
 */
export function partyConfirmationAfter(state, event, limits) {
    assertLimits(limits);
    if (state.kind === "locked")
        return state;
    const counter = counterOf(state);
    switch (event.kind) {
        case "code_requested":
        case "code_refused":
            return state;
        case "code_sent":
        case "code_pending": {
            if (!Number.isSafeInteger(event.ordinal) || event.ordinal < 1)
                throw new RangeError("a code's ordinal is a positive integer");
            if (event.ordinal <= state.codeSentCount)
                return state;
            return Object.freeze({
                kind: "pending",
                method: "sms_code",
                ordinal: event.ordinal,
                attemptsLeft: CONFIRMATION_CODE_ATTEMPT_PER_CODE,
                outcomeCount: 0,
                nonAttemptForCode: 0,
                prior: confirmationInForceOf(state),
                ...counter,
                codeSentCount: event.ordinal,
            });
        }
        case "passcode_asked": {
            if (!Number.isSafeInteger(event.ordinal) || event.ordinal < 1)
                throw new RangeError("a passcode ask's ordinal is a positive integer");
            if (event.ordinal <= state.passcodeAskCount)
                return state;
            const attemptsLeft = PASSCODE_MISS_PER_CONVERSATION - state.passcodeMissCount;
            return Object.freeze({
                kind: "pending",
                method: "passcode",
                ordinal: event.ordinal,
                attemptsLeft,
                outcomeCount: 0,
                nonAttemptForCode: 0,
                prior: confirmationInForceOf(state),
                ...counter,
                passcodeAskCount: event.ordinal,
            });
        }
        case "attempt_matched": {
            if (!freshOutcome(state, event))
                return state;
            return confirmedOf(merged(state.prior, event.method, event.atMs, event.stintOrdinal), counter);
        }
        case "attempt_missed": {
            if (!freshOutcome(state, event))
                return state;
            return missOf(state, counter, event.outcomeOrdinal, state.nonAttemptForCode, event.atMs, limits);
        }
        case "non_attempt": {
            if (!freshOutcome(state, event))
                return state;
            const counted = { ...counter, nonAttemptCount: counter.nonAttemptCount + 1 };
            if (counted.nonAttemptCount >= limits.nonAttemptPerConversation)
                return lockedOf(state.method, event.atMs, counted);
            const nonAttemptForCode = state.nonAttemptForCode + 1;
            if (nonAttemptForCode > limits.nonAttemptPerCode) {
                return missOf(state, counted, event.outcomeOrdinal, nonAttemptForCode, event.atMs, limits);
            }
            return Object.freeze({ ...state, ...counted, outcomeCount: event.outcomeOrdinal, nonAttemptForCode });
        }
        case "code_expired": {
            if (state.kind !== "pending" || state.method !== "sms_code" || state.ordinal !== event.ordinal)
                return state;
            return backOf(state.prior, counter);
        }
        case "signed_visitor_verified": {
            if (state.kind === "pending") {
                return Object.freeze({ ...state, prior: merged(state.prior, "signed_visitor", event.atMs, event.stintOrdinal) });
            }
            return confirmedOf(merged(confirmationInForceOf(state), "signed_visitor", event.atMs, event.stintOrdinal), counter);
        }
    }
}
//# sourceMappingURL=state.js.map